import { useCallback, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { v4 as uuidv4 } from 'uuid';

import { useControllableStateWithChangeCallback } from '../../../shared/hooks/controllableStateHook';

function isFile(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}

function isDirectory(
  entry: FileSystemEntry,
): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

const toFile = (entry: FileSystemFileEntry): Promise<File | undefined> => {
  return new Promise((resolve, reject) => {
    entry.file((file: File) => {
      resolve(file);
    }, reject);
  });
};

const EMPTY_RESULT: File[] = [];

const traverseFileTree = async (
  entry: FileSystemEntry,
  path = '',
): Promise<File[]> => {
  if (isFile(entry)) {
    const file = await toFile(entry);
    if (file) {
      return [file];
    } else {
      return EMPTY_RESULT;
    }
  } else if (isDirectory(entry)) {
    const dirReader = entry.createReader();
    return new Promise((resolve, reject) => {
      const filesInDir: File[] = [];
      function batch() {
        dirReader.readEntries(async entries => {
          if (entries.length === 0) {
            resolve(filesInDir);
          }

          const sattled = await Promise.allSettled(
            entries.map(e => traverseFileTree(e, path + entry.name + '/')),
          );
          const files = sattled.flatMap(result => {
            if (result.status === 'fulfilled') {
              return result.value;
            }
            console.error(result.reason);
            return EMPTY_RESULT;
          });

          filesInDir.push(...files);

          batch();
        }, reject);
      }
      batch();
    });
  }
  return EMPTY_RESULT;
};

export interface SingleFile {
  id: string;
  file: File;
}
export const useDropFile = (options?: {
  controlledFiles?: SingleFile[];
  updateFiles: (files: SingleFile[] | undefined) => void;
  filter?: (file: File) => boolean;
}) => {
  const {
    controlledFiles,
    updateFiles = (_: SingleFile[] | undefined) => {
      return;
    },
    filter = () => true,
  } = options ?? {};
  const [files = [], setFiles] = useControllableStateWithChangeCallback<
    SingleFile[]
  >(controlledFiles, updateFiles);

  const addToFiles = useCallback(
    (newFiles: File[]) => {
      setFiles(
        files.concat(
          newFiles.filter(filter).map(file => ({ id: uuidv4(), file })),
        ),
      );
    },
    [filter, setFiles, files],
  );

  const [, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { dataTransfer: DataTransfer }) {
        const temporaryFileList = item.dataTransfer.items;
        const traversedEntries = Array.from(temporaryFileList)
          .flatMap(tempFileItem => {
            const temporaryFile = tempFileItem.webkitGetAsEntry();
            return temporaryFile !== null ? [temporaryFile] : [];
          })
          .map((entry: FileSystemEntry) => traverseFileTree(entry));
        Promise.allSettled(traversedEntries).then(results => {
          const droppedFiles = results.reduce<File[]>(
            (aggregatedFiles, result) => {
              if (result.status === 'rejected') {
                console.warn('File drop failed', result.reason);
                return aggregatedFiles;
              }
              return [...aggregatedFiles, ...result.value];
            },
            [],
          );
          addToFiles(droppedFiles);
        });
      },
    }),
    [filter],
  );

  const deleteFromFiles = useCallback(
    (id: string) => {
      setFiles(files.filter(file => file.id !== id));
    },
    [files, setFiles],
  );

  return {
    files,
    addToFiles,
    deleteFromFiles,
    drop,
  };
};

export interface FileChunk {
  id: string;
  files: File[];
}
export const useDropFileChunk = (options?: {
  controlledFileChunks?: FileChunk[];
  updateFiles: (files: FileChunk[] | undefined) => void;
  filter?: (file: File) => boolean;
}) => {
  const {
    controlledFileChunks,
    updateFiles = () => {
      return;
    },
    filter = () => true,
  } = options ?? {};
  const [fileChunks = [], setFileChunks] =
    useControllableStateWithChangeCallback(controlledFileChunks, updateFiles);

  const addToFileChunks = useCallback(
    (files: File[]) => {
      const newFiles = files.filter(filter);
      if (newFiles.length) {
        setFileChunks(fileChunks.concat([{ id: uuidv4(), files: newFiles }]));
      }
    },
    [filter, fileChunks, setFileChunks],
  );

  // recursive tree traversing for directories
  const [, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { dataTransfer: DataTransfer }) {
        const temporaryFileList = item.dataTransfer.items;
        console.log(temporaryFileList);
        const traversedEntries = Array.from(temporaryFileList)
          .flatMap(tempFileItem => {
            const temporaryFile = tempFileItem.webkitGetAsEntry();
            return temporaryFile !== null ? [temporaryFile] : [];
          })
          .map((entry: FileSystemEntry) => traverseFileTree(entry));
        Promise.allSettled(traversedEntries).then(results => {
          const aggregatedFiles = results.reduce<File[]>(
            (wholeFiles, result) => {
              if (result.status === 'rejected') {
                console.warn('File drop failed', result.reason);
                return wholeFiles;
              }

              return [
                ...wholeFiles,
                ...result.value.filter(
                  ({ type, name }) =>
                    type === 'application/dicom' ||
                    name.split('.').slice(-1)[0] === 'dcm',
                ),
              ];
            },
            [],
          );
          addToFileChunks(aggregatedFiles);
        });
      },
    }),
    [filter],
  );

  const deleteFromFileChunks = useCallback(
    (id: string) => {
      setFileChunks(fileChunks.filter(file => file.id !== id));
    },
    [fileChunks, setFileChunks],
  );

  return {
    fileChunks,
    addToFileChunks,
    deleteFromFileChunks,
    drop,
  };
};
